import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  Linkedin,
  Twitter,
  Facebook,
  ChevronRight,
  CheckCircle,
  List,
} from 'lucide-react';
import { postsAPI } from '../lib/api';
import { formatDate, LP_URL } from '../lib/utils';
import CTASection from '../components/shared/CTASection';
import { usePageMeta, ArticleSchema, FAQSchema, BreadcrumbSchema } from '../components/shared/SEO';
import DOMPurify from 'dompurify';

// Extract H2/H3 headings from HTML and inject anchor IDs
function parseHeadings(html) {
  if (!html) return { headings: [], html };
  const headings = [];
  let counter = 0;
  const slugCounts = {};

  const processed = html.replace(/<(h2)([^>]*)>(.*?)<\/\1>/gi, (match, tag, attrs, text) => {
    const clean = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
    if (!clean) return match;
    let base = clean
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 60);
    if (slugCounts[base] !== undefined) {
      slugCounts[base]++;
      base = `${base}-${slugCounts[base]}`;
    } else {
      slugCounts[base] = 0;
    }
    const id = `heading-${base}`;
    headings.push({ id, text: clean });
    counter++;
    return `<${tag}${attrs} id="${id}">${text}</${tag}>`;
  });

  return { headings, html: processed };
}

const ArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await postsAPI.getBySlug(slug);
      setPost(response.data);
    } catch (err) {
      setError('Artigo não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const { headings, html: contentWithIds } = useMemo(
    () => parseHeadings(post?.content),
    [post?.content]
  );

  const handleScrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 100; // account for sticky navbar
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  usePageMeta({
    title: post?.title,
    description: post?.excerpt,
    canonical: post ? `/blog/${post.slug}` : undefined,
  });

  const handleShare = (platform) => {
    const urls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || '')}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="pt-28 pb-10">
        <div className="container-apex max-w-4xl">
          <div className="skeleton h-8 w-32 mb-4" />
          <div className="skeleton h-12 w-full mb-4" />
          <div className="skeleton h-6 w-3/4 mb-8" />
          <div className="skeleton h-96 w-full rounded-xl mb-8" />
          <div className="space-y-4">
            <div className="skeleton h-4 w-full" />
            <div className="skeleton h-4 w-full" />
            <div className="skeleton h-4 w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-28 pb-10">
        <div className="container-apex max-w-4xl text-center">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
            Artigo não encontrado
          </h1>
          <p className="text-slate-600 mb-8">
            O artigo que você procura não existe ou foi removido.
          </p>
          <Button asChild className="btn-primary">
            <Link to="/blog">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar ao blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <article data-testid="article-page">
      <ArticleSchema post={post} />
      <FAQSchema items={post.faq_items} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: post.category_name || 'Categoria', url: '/blog' },
        { name: post.title },
      ]} />
      {/* Breadcrumbs */}
      <div className="bg-slate-50 pt-24 pb-4">
        <div className="container-apex">
          <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-primary">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            {post.category_names && post.category_names.length > 0 ? (
              post.category_names.map((name, i) => (
                <React.Fragment key={i}>
                  <span className="text-slate-600">{name}</span>
                  {i < post.category_names.length - 1 && <span className="text-slate-400">|</span>}
                </React.Fragment>
              ))
            ) : (
              <Link to={`/blog?category=${post.category_id}`} className="hover:text-primary">
                {post.category_name}
              </Link>
            )}
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-700 truncate max-w-xs">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <header className="bg-slate-50 pb-12">
        <div className="container-apex max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-primary mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao blog
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            {post.category_names && post.category_names.length > 0 ? (
              post.category_names.map((name, i) => (
                <span key={i} className="category-badge">{name}</span>
              ))
            ) : (
              <span className="category-badge">{post.category_name}</span>
            )}
          </div>
          
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-testid="article-title">
            {post.title}
          </h1>
          
          <p className="text-xl text-slate-600 mb-6 text-justify">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{post.reading_time} min de leitura</span>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="container-apex max-w-5xl -mt-4">
          <img
            src={post.featured_image}
            alt={post.featured_image_alt || post.title}
            className="w-full rounded-2xl shadow-xl aspect-[21/9]"
            style={{ objectFit: 'cover', objectPosition: post.featured_image_position || 'center' }}
            data-testid="article-image"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className="section-padding">
        <div className="container-apex">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-8 min-w-0">
              {/* Key Takeaways */}
              {post.key_takeaways && post.key_takeaways.length > 0 && (
                <div className="bg-[#1e4e7e]/5 border border-[#1e4e7e]/10 rounded-xl p-6 mb-8" data-testid="key-takeaways">
                  <h2 className="font-heading font-semibold text-lg text-[#1e4e7e] mb-4">
                    Principais pontos do artigo
                  </h2>
                  <ul className="space-y-3">
                    {post.key_takeaways.map((takeaway, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#007f73] mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Table of Contents */}
              {headings.length >= 2 && (
                <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-8" data-testid="article-toc">
                  <h2 className="font-heading font-semibold text-base text-slate-900 mb-3 flex items-center gap-2">
                    <List className="w-4 h-4 text-[#1e4e7e]" />
                    Neste artigo
                  </h2>
                  <ul className="space-y-1.5">
                    {headings.map((h) => (
                      <li key={h.id}>
                        <button
                          onClick={() => handleScrollTo(h.id)}
                          className="text-left w-full text-sm transition-colors hover:text-[#1e4e7e] text-slate-700 font-medium"
                          data-testid={`toc-item-${h.id}`}
                        >
                          {h.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {/* Article Content */}
              <div
                className="prose-apex"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
                  (contentWithIds || '').replace(/&nbsp;/g, ' '),
                  { ADD_ATTR: ['id'] }
                ) }}
                data-testid="article-content"
              />

              {/* FAQ Section */}
              {post.faq_items && post.faq_items.length > 0 && (
                <div className="mt-12 border-t border-slate-200 pt-8" data-testid="article-faq">
                  <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
                    Perguntas Frequentes
                  </h2>
                  <div className="space-y-6">
                    {post.faq_items.map((faq, index) => (
                      <div key={index} className="bg-slate-50 rounded-xl p-6">
                        <h3 className="font-semibold text-slate-900 mb-2">
                          {faq.question}
                        </h3>
                        <p className="text-slate-600">
                          {faq.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Box */}
              <div className="mt-12 bg-slate-50 rounded-xl p-6 flex gap-4" data-testid="author-box">
                <div className="w-16 h-16 rounded-full bg-[#1e4e7e] flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Escrito por</p>
                  <h4 className="font-heading font-semibold text-slate-900 mb-2">
                    {post.author_name}
                  </h4>
                  <p className="text-slate-600 text-sm">
                    {post.author_bio}
                  </p>
                </div>
              </div>

              {/* Share */}
              <div className="mt-8 flex items-center gap-4">
                <span className="text-sm text-slate-500 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Compartilhar:
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-[#0077b5] hover:text-white transition-colors"
                    aria-label="Compartilhar no LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-[#1da1f2] hover:text-white transition-colors"
                    aria-label="Compartilhar no Twitter"
                  >
                    <Twitter className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-[#4267B2] hover:text-white transition-colors"
                    aria-label="Compartilhar no Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-28 space-y-8">
                {/* CTA Card */}
                <div className="bg-gradient-to-br from-[#1e4e7e] to-[#007f73] rounded-xl p-6 text-white">
                  <h3 className="font-heading font-semibold text-xl mb-3">
                    Sua operação médica pode ser mais eficiente
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    A Apex Medical Hub ajuda clínicas e consultórios com gestão, finanças e estratégia.
                  </p>
                  <Button
                    asChild
                    className="w-full bg-white text-[#1e4e7e] hover:bg-white/90"
                  >
                    <a href={LP_URL} target="_blank" rel="noopener noreferrer">
                      Quero falar com a Apex
                    </a>
                  </Button>
                </div>

                {/* Related Posts */}
                {post.related_posts && post.related_posts.length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-slate-100">
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                      Artigos relacionados
                    </h3>
                    <div className="space-y-4">
                      {post.related_posts.map((related) => (
                        <Link
                          key={related.id}
                          to={`/blog/${related.slug}`}
                          className="block group"
                        >
                          <h4 className="font-medium text-slate-900 group-hover:text-primary transition-colors line-clamp-2">
                            {related.title}
                          </h4>
                          <p className="text-sm text-slate-500 mt-1">
                            {related.reading_time} min de leitura
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="bg-white rounded-xl p-6 border border-slate-100">
                    <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* End CTA */}
      <CTASection />
    </article>
  );
};

export default ArticlePage;
